import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/* ----------------------------------------------------------------------------
 * useFocusTrap — while `enabled`, keeps keyboard focus inside the returned
 * container ref, closes on ESC, and restores focus to the previously-focused
 * element on teardown. Used by the mobile drawer + copilot bottom-sheet; a
 * no-op on desktop (enabled=false) so inline surfaces behave normally.
 * The container should carry tabIndex={-1} so it can receive focus itself.
 * -------------------------------------------------------------------------- */
export function useFocusTrap<T extends HTMLElement>(enabled: boolean, onClose: () => void) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!enabled) return;
    const node = ref.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const list = (): HTMLElement[] => {
      if (!node) return [];
      return Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
    };

    const raf = requestAnimationFrame(() => {
      const els = list();
      (els[0] ?? node)?.focus();
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !node) return;
      const els = list();
      if (els.length === 0) {
        e.preventDefault();
        node.focus();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || active === node)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, [enabled, onClose]);

  return ref;
}
